import { lazy, Suspense } from "react";
import HeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";
import CitySEOHead from "@/components/feature/CitySEOHead";

const CitiesDirectory = lazy(() => import("./components/CitiesDirectory"));
const StateResources = lazy(() => import("./components/StateResources"));
const FAQSection = lazy(() => import("./components/FAQSection"));
const FinalCTA = lazy(() => import("./components/FinalCTA"));
import { faqs } from "./components/FAQSection";

const delawareServices = [
  { name: "Emergency Passport", description: "Urgent passport processing for Delaware travelers with immediate departure needs, as fast as 24 hours." },
  { name: "Passport Renewal", description: "Fast passport renewal for Delaware U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
  { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
  { name: "Lost or Stolen Passport", description: "Passport replacement service for Delaware residents with lost or stolen passports." },
  { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
  { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
];

export default function DelawarePage() {
  return (
    <>
      <CitySEOHead
        city="Delaware"
        state="Delaware"
        stateAbbr="DE"
        route="/states/delaware"
        title="Expedited & Emergency Passport Services Delaware | Fast Passport Renewal DE | Fast Passport Center"
        description="Fast Passport Center provides emergency, expedited, and same-day passport services across Delaware — Wilmington, Dover, Newark, and beyond. As fast as 24 hours. A+ BBB rated, registered State Dept. courier."
        geoPlacename="Delaware"
        geoPosition="38.9108;-75.5277"
        faqs={faqs.map((f) => ({ q: f.question, a: f.answer }))}
        servicesSchema={delawareServices}
        keywords="expedited passport Delaware, emergency passport Delaware, same day passport DE, fast passport Wilmington, passport renewal Dover, passport service Newark Delaware"
      />
      <div className="min-h-screen bg-white">
        <HeroSection />
        <Suspense
          fallback={
            <div className="h-64 flex items-center justify-center bg-gray-50">
              <div className="animate-pulse text-gray-400 text-sm">Loading cities...</div>
            </div>
          }
        >
          <CitiesDirectory />
        </Suspense>

        <section className="bg-[#F7F8FA] py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-14">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">
                Passport Services
              </p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
                How We Serve Delaware
              </h2>
              <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
                From Wilmington&apos;s corporate banking corridor to the Delaware beaches, from Dover Air Force Base to the University of Delaware campus — we help First State travelers get their passports fast. No regional agency in Delaware means residents rely on Philadelphia or DC. Our courier service eliminates the drive entirely.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: "ri-flight-takeoff-line",
                  title: "Emergency Same-Day Service",
                  desc: "For Delaware travelers with flights within 72 hours. We hand-carry your application to the nearest regional agency.",
                },
                {
                  icon: "ri-time-line",
                  title: "Expedited 2–9 Business Days",
                  desc: "Standard expedited processing with overnight shipping and tracking for all Delaware residents.",
                },
                {
                  icon: "ri-file-list-3-line",
                  title: "New Passport Applications",
                  desc: "First-time applicants in Delaware — we handle document prep, photos, and acceptance facility scheduling.",
                },
                {
                  icon: "ri-refresh-line",
                  title: "Passport Renewals",
                  desc: "Delaware renewals by mail or courier. We ensure your application is complete to avoid delays.",
                },
                {
                  icon: "ri-user-add-line",
                  title: "Child & Minor Passports",
                  desc: "Delaware families with kids under 16 — we guide you through parental consent and documentation.",
                },
                {
                  icon: "ri-error-warning-line",
                  title: "Lost, Stolen & Damaged",
                  desc: "Fast replacement for Delaware residents who need a new passport quickly.",
                },
              ].map((svc, i) => (
                <div
                  key={i}
                  className="bg-white rounded-lg p-7 border border-gray-100 hover:border-[#F6C344]/40 transition-all duration-200"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-5">
                    <i className={`${svc.icon} text-[#1A365D] text-xl`}></i>
                  </div>
                  <h3 className="font-bold text-[#1A365D] text-base mb-2">{svc.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{svc.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Suspense
          fallback={
            <div className="h-64 flex items-center justify-center bg-white">
              <div className="animate-pulse text-gray-400 text-sm">Loading resources...</div>
            </div>
          }
        >
          <StateResources />
        </Suspense>
        <Suspense
          fallback={
            <div className="h-64 flex items-center justify-center bg-gray-50">
              <div className="animate-pulse text-gray-400 text-sm">Loading FAQs...</div>
            </div>
          }
        >
          <FAQSection />
        </Suspense>

        <section className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <SEOCrossLinks state="DE" showCities={false} />
          </div>
        </section>

        <Suspense
          fallback={
            <div className="h-64 flex items-center justify-center bg-[#0F2240]">
              <div className="animate-pulse text-gray-400 text-sm">Loading...</div>
            </div>
          }
        >
          <FinalCTA />
        </Suspense>
      </div>
    </>
  );
}